import { useEffect, useState } from 'react';

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Returns true once the referenced element has entered the viewport
 * (intersecting at ≥ threshold). `once: true` unobserves after the
 * first intersection so the boolean stays true while the element
 * scrolls back out. SSR, browsers without IntersectionObserver, and
 * users with prefers-reduced-motion all get `true` immediately so
 * nothing stays hidden waiting for scroll.
 */
export function useInView(ref, { threshold = 0.25, once = true, rootMargin = '0px' } = {}) {
  const [inView, setInView] = useState(() => {
    if (typeof window === 'undefined') return true;
    if (typeof window.IntersectionObserver === 'undefined') return true;
    if (prefersReducedMotion()) return true;
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (typeof window.IntersectionObserver === 'undefined') return undefined;
    if (prefersReducedMotion()) return undefined;
    const el = ref.current;
    if (!el) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) io.unobserve(entry.target);
          } else if (!once) {
            setInView(false);
          }
        }
      },
      { threshold, rootMargin },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [ref, threshold, once, rootMargin]);

  return inView;
}
